import React, { useCallback, useState } from 'react';
import { NextPageContext } from 'next';

import {
  Accordion,
  Amenities,
  Breadcrumbs,
  ContactButtons,
  FAQs,
  HeroLocation,
  LocationsInSection,
  PhotoModal,
} from '../../../../components';
import { useSiteQuery } from '../../../../hooks';
import { getLocationTitle } from '../../../../lib/FunctionLib';
import ErrorPage from '../../../_error';

import Page from '../../../[...all]';
import query from './index.graphql';
import styles from "../../../nursing-homes/locations/[uid]/index.module.scss";

interface Props {
  uri: string;
  slug: string;
}

const getCareServiceName = (slug: string) => {
  return slug
    .split('-')
    .map((i) => i[0].toUpperCase() + i.substring(1))
    .join(' ');
};

const OtherServiceLocation = ({ uri, slug }: Props) => {
  const [{ data, error }] = useSiteQuery<OtherServiceLocationQuery>({ query, variables: { slug } });
  const [photoModalOpen, openPhotoModal] = useState(false);

  const togglePhotoModal = useCallback(() => {
    const toSet = !photoModalOpen;
    openPhotoModal(toSet);
  }, [photoModalOpen]);

  // function onGalleryClick(e: React.MouseEvent<Element> | React.KeyboardEvent<Element>) {
  //   if (e.type !== 'keypress' || (e as React.KeyboardEvent).key === 'Enter') {
  //     togglePhotoModal();
  //   }
  // }

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  const entry = data?.entry;
  let headings = [
    { title: 'Overview', slug: 'home-overview' },
    { title: 'Location', slug: 'location' },
    { title: 'Fees & Pricing', slug: 'fees-pricing' },
    { title: 'Frequently Asked Questions', slug: 'faqs' },
  ];

  if (!entry?.feesPricing) {
    headings = headings.filter((heading) => heading.slug !== 'fees-pricing');
  }

  if (entry) {
    const title = getLocationTitle(entry);
    const photos = entry.photos?.filter((p): p is AssetFragment => p !== null && p !== undefined);
    const careServiceName = getCareServiceName(entry.careService!);

    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Other Care Services', uri: '/other-care-services' },
      { title: careServiceName, uri: `/other-care-services/${entry.careService!}` },
      { title, uri: `/${entry.uri!}` },
    ];

    return (
      <>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <HeroLocation
          sectionHeading={careServiceName}
          title={title}
          headline={entry.headline!}
          headings={headings}
          photos={photos}
          showContactButtons={entry.showContactButtons ?? false}
          additionalClasses={'override-bg-stone-signature'}
        />
        <LocationsInSection
          customTitle="Explore our other services"
          current={entry}
          all={data?.allLocations?.filter((location) => location?.careService === entry.careService)}
        />
        <div className="section left">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div id="home-overview" className="sectionHeading">
                  Overview
                </div>
                <div dangerouslySetInnerHTML={{ __html: entry.body! }} />
                <div>
                  <p>
                    <strong>Get in touch for more details</strong>
                  </p>
                  <ContactButtons />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Amenities title={entry.title!} address={entry.address!} amenities={[]} />

        {/* Fees & Pricing section - from pages/nursing-homes/locations/[uid]/index.tsx */}
        {
          entry?.feesPricing ?
            <div className="section section-white">
              <div className="container">
                <div id="fees-pricing" className="sectionHeading">
                  Fees & Pricing
                </div>
                <div className="row">
                  <div className="col-lg-6">
                    <div dangerouslySetInnerHTML={{ __html: entry.feesPricing! }} />
                  </div>
                  <div className="col-lg-6">
                    <div className={styles.accordionBlock}>
                      {entry.feesPricingAccordion?.map(
                        (item) =>
                          item && (
                            <Accordion key={item!.title!} title={item!.title!} blockStyle>
                              <div dangerouslySetInnerHTML={{ __html: item!.text! }} />
                            </Accordion>
                          ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            : null
        }

        <FAQs topic={careServiceName} />
        {photoModalOpen && photos && <PhotoModal photos={photos} handleClose={togglePhotoModal} />}
      </>
    );
  }

  return <Page uri={uri} />;
};

OtherServiceLocation.getInitialProps = ({ req }: NextPageContext) => ({
  uri: req?.url?.replace(/^\//, '').replace(/\?.*$/, '') || '404',
  slug: req?.url?.substring(req?.url?.lastIndexOf('/') + 1).replace(/\?.*$/, ''),
});

export default OtherServiceLocation;
